import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const bookingSchema = z.object({
  customer_name: z.string().trim().min(1).max(100),
  customer_email: z.string().trim().max(255).optional().or(z.literal("")),
  customer_phone: z.string().trim().min(4).max(30),
  pickup: z.string().trim().min(1).max(300),
  dropoff: z.string().trim().min(1).max(300),
  pickup_date: z.string().trim().min(1).max(50),
  pickup_time: z.string().trim().min(1).max(50),
  trip_type: z.string().trim().min(1).max(20),
  vehicle: z.string().trim().min(1).max(50),
  flight_number: z.string().trim().max(30).optional().or(z.literal("")),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
  estimated_fare: z.number().nonnegative().max(100000).optional(),
  distance_km: z.number().nonnegative().max(10000).optional(),
  duration_min: z.number().nonnegative().max(100000).optional(),
});

export type BookingInput = z.infer<typeof bookingSchema>;

export const submitBooking = createServerFn({ method: "POST" })
  .inputValidator((data: BookingInput) => bookingSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: inserted, error } = await supabaseAdmin
      .from("bookings")
      .insert({
        customer_name: data.customer_name,
        customer_email: data.customer_email || "not provided",
        customer_phone: data.customer_phone,
        pickup: data.pickup,
        dropoff: data.dropoff,
        pickup_date: data.pickup_date,
        pickup_time: data.pickup_time,
        trip_type: data.trip_type,
        vehicle: data.vehicle,
        flight_number: data.flight_number || null,
        notes: data.notes || null,
        estimated_fare: data.estimated_fare ?? null,
        distance_km: data.distance_km ?? null,
        duration_min: data.duration_min ?? null,
      })
      .select("id")
      .single();

    if (error) {
      console.error("[bookings] insert failed", error);
      throw new Error("Could not save booking. Please try again.");
    }

    // Best-effort owner notification through Lovable managed email.
    // Sends once the sender domain (notify.sepremiumcabs.com) is verified.
    let emailSent = false;
    let emailError: string | null = null;
    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      const result = await sendTemplateEmail(
        "booking-notification",
        "sepremiumcabs@gmail.com",
        {
          idempotencyKey: `booking-notification-${inserted?.id}`,
          replyTo: data.customer_email || undefined,
          templateData: {
            customerName: data.customer_name,
            customerEmail: data.customer_email || "Not provided",
            customerPhone: data.customer_phone,
            pickup: data.pickup,
            dropoff: data.dropoff,
            pickupDate: data.pickup_date,
            pickupTime: data.pickup_time,
            tripType: data.trip_type,
            vehicle: data.vehicle,
            flightNumber: data.flight_number || undefined,
            notes: data.notes || undefined,
            estimatedFare: data.estimated_fare ?? undefined,
            distanceKm: data.distance_km ?? undefined,
            durationMin: data.duration_min ?? undefined,
            bookingId: inserted?.id,
          },
        },
      );
      emailSent = result.sent;
      if (!result.sent) emailError = result.reason;
    } catch (err) {
      emailError = err instanceof Error ? err.message : String(err);
      console.warn("[bookings] email send failed:", emailError);
    }

    return { ok: true as const, id: inserted?.id, emailSent, emailError };
  });